import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import { extractNestedAnswers } from "@/encounters/components/PostpartumEncounter/utils/postpartum";
import {
  PostpartumEncounterQuestionnaireURLs,
  PostpartumSleepQuestionsDisplayNames,
  PostpartumSleepQuestionsLinkIds,
} from "@/encounters/constants/postpartum-encounter";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";

const SleepDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          PostpartumEncounterQuestionnaireURLs.Sleep,
          encounterId!,
        );

        if (!foundAmendmentHistory) return;

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  // If no sleep data has ever been recorded
  if (!currentVersion) {
    return (
      <Paper p="md" withBorder>
        <Stack mb="lg">
          <Title order={2}>Sleep</Title>
        </Stack>
        <TextWrapper py="lg" c="gray.6" ta="center">
          No sleep data has been recorded for this encounter.
        </TextWrapper>
      </Paper>
    );
  }

  // If sleep data exists but no previous version to compare against,
  // create an empty previous version to reuse the existing diff logic
  const effectivePreviousVersion =
    previousVersion || ({ item: [] } as unknown as QuestionnaireResponse);

  // Extract values for all sleep fields using extractNestedAnswers
  const previousAnswers = extractNestedAnswers(effectivePreviousVersion);
  const currentAnswers = extractNestedAnswers(currentVersion);

  const previousSleepHours =
    previousAnswers[PostpartumSleepQuestionsLinkIds.PostpartumSleepHours]?.join(", ") || "";
  const currentSleepHours =
    currentAnswers[PostpartumSleepQuestionsLinkIds.PostpartumSleepHours]?.join(", ") || "";

  const previousSleepWakeUp =
    previousAnswers[PostpartumSleepQuestionsLinkIds.PostpartumSleepWakeUp]?.join(", ") || "";
  const currentSleepWakeUp =
    currentAnswers[PostpartumSleepQuestionsLinkIds.PostpartumSleepWakeUp]?.join(", ") || "";

  const previousSleepFallingAsleep =
    previousAnswers[PostpartumSleepQuestionsLinkIds.PostpartumSleepFallingAsleep]?.join(", ") || "";
  const currentSleepFallingAsleep =
    currentAnswers[PostpartumSleepQuestionsLinkIds.PostpartumSleepFallingAsleep]?.join(", ") || "";

  const previousSleepStayingAsleep =
    previousAnswers[PostpartumSleepQuestionsLinkIds.PostpartumSleepStayingAsleep]?.join(", ") || "";
  const currentSleepStayingAsleep =
    currentAnswers[PostpartumSleepQuestionsLinkIds.PostpartumSleepStayingAsleep]?.join(", ") || "";

  const previousSleepBreathing =
    previousAnswers[PostpartumSleepQuestionsLinkIds.PostpartumSleepBreathing]?.join(", ") || "";
  const currentSleepBreathing =
    currentAnswers[PostpartumSleepQuestionsLinkIds.PostpartumSleepBreathing]?.join(", ") || "";

  const previousSleepNotes =
    previousAnswers[PostpartumSleepQuestionsLinkIds.PostpartumSleepNotes]?.join(", ") || "";
  const currentSleepNotes =
    currentAnswers[PostpartumSleepQuestionsLinkIds.PostpartumSleepNotes]?.join(", ") || "";

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Sleep</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSleepQuestionsDisplayNames.PostpartumSleepHours}
                </TextWrapper>
                <WordsDiff showRemoved current={currentSleepHours} previous={previousSleepHours} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSleepQuestionsDisplayNames.PostpartumSleepWakeUp}
                </TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentSleepWakeUp}
                  previous={previousSleepWakeUp}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSleepQuestionsDisplayNames.PostpartumSleepFallingAsleep}
                </TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentSleepFallingAsleep}
                  previous={previousSleepFallingAsleep}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSleepQuestionsDisplayNames.PostpartumSleepStayingAsleep}
                </TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentSleepStayingAsleep}
                  previous={previousSleepStayingAsleep}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSleepQuestionsDisplayNames.PostpartumSleepBreathing}
                </TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentSleepBreathing}
                  previous={previousSleepBreathing}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSleepQuestionsDisplayNames.PostpartumSleepNotes}
                </TextWrapper>
                <WordsDiff showRemoved current={currentSleepNotes} previous={previousSleepNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSleepQuestionsDisplayNames.PostpartumSleepHours}
                </TextWrapper>
                <WordsDiff current={currentSleepHours} previous={previousSleepHours} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSleepQuestionsDisplayNames.PostpartumSleepWakeUp}
                </TextWrapper>
                <WordsDiff current={currentSleepWakeUp} previous={previousSleepWakeUp} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSleepQuestionsDisplayNames.PostpartumSleepFallingAsleep}
                </TextWrapper>
                <WordsDiff
                  current={currentSleepFallingAsleep}
                  previous={previousSleepFallingAsleep}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSleepQuestionsDisplayNames.PostpartumSleepStayingAsleep}
                </TextWrapper>
                <WordsDiff
                  current={currentSleepStayingAsleep}
                  previous={previousSleepStayingAsleep}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSleepQuestionsDisplayNames.PostpartumSleepBreathing}
                </TextWrapper>
                <WordsDiff current={currentSleepBreathing} previous={previousSleepBreathing} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSleepQuestionsDisplayNames.PostpartumSleepNotes}
                </TextWrapper>
                <WordsDiff current={currentSleepNotes} previous={previousSleepNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default SleepDiff;
